import { coordToArr } from "@nova/calculate-geospatial"
import {
    DownloadButtonData,
    IntegrationOption,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createPolygonGPX, createPolygonKML } from "@nova/data-create-polygon-kml"
import { config } from "@nova/configuration/browser"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"
import { polygonTransformer } from "integrations/geojson/transformers/polygon"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { useCallback, useState } from "react"
import { download, downloadUri } from "tools/browser/download"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { selectUserID } from "user/profile/selectors"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { Perimeter } from "@nova/protobuf"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { getDate } from "features/map/inspector-panel/module"
import { TimeFormat } from "@nova/format"
import { useExportToCalTopo } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/caltopo/useExportToCalTopo"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { IntegrationExportEntity } from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import {
    selectActiveWorkspaceId,
    selectWorkspaceDateFormat,
} from "features/data-management/workspace/WorkspaceManagementSlice"

export const useFirePerimeterDownloadButtonData = (): DownloadButtonData => {
    const dispatch = useAppDispatch()
    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(-1)
    const id = selectedLayerId || ""
    const perimeter = useAppSelector(selectFirePerimeterById(id))
    const { organizationSettingCRS } = config.use("organizationSettingCRS")
    const userId = useAppSelector(selectUserID)
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const flight = useAppSelector(selectFirePerimeterById(id)) as Perimeter

    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.agol,
            authType: "oauth",
            userId,
        })

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.tak,
            authType: "certificate",
            userId,
        })

    const [agolExportError, setAGOLExportError] = useState<string>("Failed to export")

    const { exportToAGOL } = useExportToAGOL()
    const { data: caltopoAuths, invalidateAuthorizationsCache: invalidateCalTopo } =
        useIntegrationAuthorizations({
            authType: "caltopo",
            integration: IntegrationType.caltopo,
            workspaceId: workspaceId,
        })
    const { exportToTAK } = useExportToTAK()
    const { exportToCalTopo } = useExportToCalTopo()

    const downloadKML = useCallback(() => {
        if (!perimeter) return

        download(
            perimeter.name.toLowerCase().endsWith(".kml")
                ? perimeter.name
                : perimeter.name + ".kml",
            "application/vnd.google-earth.kml+xml",
            createPolygonKML(
                perimeter.name.split(".")[0],
                perimeter.polygons.map((e) => ({
                    coords: e.coordinates.map((e) => coordToArr(e) as [number, number]),
                    color: perimeter.outline
                        ? decodeNumberToColor(perimeter.outline, { isKml: true })
                        : "FF1A1AE5",
                    width: perimeter.width,
                }))
            )
        )
    }, [perimeter])

    const downloadSHP = useCallback(async () => {
        if (!perimeter?.polygons) return

        const polygons = perimeter?.polygons.map(({ coordinates }) => ({
            name: "Test",
            points: coordinates.map(coordToArr) as [number, number][],
        }))

        const data = polygonTransformer(polygons, coordinateSystems[organizationSettingCRS].proj4)
        const type = "geometry"

        const res = await fetch(
            "/api/ogr2ogr?crs=" + coordinateSystems[organizationSettingCRS].code + "&type=" + type,
            {
                method: "POST",
                body: new File([new Blob([JSON.stringify(data)])], `${type}.geojson`),
            }
        )
        if (res.status >= 300) {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create shapefile.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
        const blob = await res.blob()

        await downloadUri(perimeter.name.split(".")[0] + ".zip", URL.createObjectURL(blob))
    }, [perimeter?.polygons, perimeter?.name, organizationSettingCRS, dispatch])

    const downloadGPX = useCallback(() => {
        if (!perimeter) return

        const originalNameLowerCase = perimeter.name.toLowerCase()
        const baseName =
            originalNameLowerCase.endsWith(".kml") || originalNameLowerCase.endsWith(".gpx")
                ? perimeter.name.split(".").slice(0, -1).join(".")
                : perimeter.name

        download(
            baseName + ".gpx",
            "application/gpx+xml",
            createPolygonGPX(baseName, perimeter.polygons)
        )
    }, [perimeter])

    const baseOptions: NestedDownloadData = {
        KML: { callback: downloadKML },
        SHP: { callback: downloadSHP },
        GPX: { callback: downloadGPX },
    }

    const integrationOptions: Record<string, IntegrationOption> = {}

    if (agolAuths?.at(0) && fireId && workspaceId) {
        const auth = agolAuths.at(0)
        const settings = auth?.settings as AGOLSettings
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    setAGOLExportError("Internal error occured")
                }

                if (isAuthExpired(auth)) {
                    try {
                        await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    } catch (e) {
                        setAGOLExportError("Failed to authenticate")
                        throw e
                    }
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }

                return exportToAGOL({
                    entities: [{ id: id, type: IntegrationExportEntity.Perimeter }],
                    projectId: fireId,
                    workspaceId,
                })
                    .then(() => {
                        invalidateAGOL()
                    })
                    .catch((e) => {
                        setAGOLExportError("Failed to export")
                        throw e
                    })
            },
            successMessage: "Success",
            failureMessage: agolExportError,
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : null
            },
        }
    }

    if (takAuths?.at(0) && fireId && workspaceId) {
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    entities: [{ id: id, type: IntegrationExportEntity.Perimeter }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : null
            },
        }
    }

    if (caltopoAuths?.at(0) && fireId && workspaceId) {
        const auth = caltopoAuths.at(0)
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions["Send to CalTopo"] = {
            callback: async () => {
                if (!auth) return

                return await exportToCalTopo({
                    entities: [{ id: id, type: IntegrationExportEntity.Perimeter }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateCalTopo()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "caltopo",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    return {
        exportOptions: baseOptions,
        integrationOptions: Object.entries(integrationOptions).length
            ? integrationOptions
            : undefined,
    }
}
